import type { RigaDaOrdinare, RigaDaPreparare } from '../../api/acquisti';
import { numeroDecimale } from '../../lib/numeri';

/*  Le regole del pannello «da chi comprare», fuori dal componente.

    Stanno qui e non dentro il JSX perché sono le due cose che il pannello deve azzeccare
    — che ordini verranno creati, e cosa si manda al backend — e dentro un componente non
    si possono provare senza montare mezzo browser. Il difetto che questo ha già evitato:
    una riga con la quantità lasciata in bianco compariva nel riepilogo «verranno creati
    2 ordini» perché `Number('')` fa zero, e poi la validazione la rifiutava.  */

/** Cosa decide chi compra su una riga: da chi, quanto e a che prezzo. */
export interface Decisione {
  fornitore: string | null;
  /** Il nome di un fornitore scelto fuori dalle offerte, che il backend non ci ha mandato. */
  altroNome: string | null;
  quantita: string;
  costoNetto: string;
  codiceArticoloFornitore: string | null;
  esclusa: boolean;
}

export interface GruppoFornitore {
  codice: string;
  nome: string;
  righe: number;
  totale: number;
}

/** Preselezionata la migliore di OGGI, non quella suggerita quando la riga è nata:
 *  fra le due può essere passato un listino nuovo, e chi compra parte dal presente. */
export function decisioneIniziale(riga: RigaDaPreparare): Decisione {
  const migliore = riga.offerte.find((o) => o.migliore) ?? riga.offerte[0];

  return {
    fornitore: migliore?.codiceFornitoreMago ?? null,
    altroNome: null,
    quantita: String(riga.daOrdinare),
    costoNetto: migliore ? String(migliore.costoNetto) : '',
    codiceArticoloFornitore: migliore?.codiceArticoloFornitore ?? null,
    esclusa: false,
  };
}

/** Una riga entra in un ordine solo se è ordinabile, non esclusa, e completa. */
function pronta(riga: RigaDaPreparare, decisione: Decisione | undefined) {
  if (!riga.ordinabile || !decisione || decisione.esclusa || !decisione.fornitore) return null;

  const quantita = numeroDecimale(decisione.quantita);
  const costo = numeroDecimale(decisione.costoNetto);
  if (quantita === null || quantita <= 0 || costo === null || costo < 0) return null;

  return { fornitore: decisione.fornitore, quantita, costo, decisione };
}

/**
 * Che ordini verranno creati: uno per fornitore, non uno per riga. Si mostra prima di
 * creare, perché è la regola che sorprende, e vederla applicata evita la domanda
 * «perché mi ha fatto due ordini».
 */
export function raggruppaPerFornitore(
  righe: RigaDaPreparare[],
  decisioni: Record<string, Decisione>,
): GruppoFornitore[] {
  const per = new Map<string, GruppoFornitore>();

  for (const riga of righe) {
    const buona = pronta(riga, decisioni[riga.rigaRichiestaId]);
    if (!buona) continue;

    const nome =
      buona.decisione.altroNome ??
      riga.offerte.find((o) => o.codiceFornitoreMago === buona.fornitore)?.fornitore ??
      buona.fornitore;

    const gruppo = per.get(buona.fornitore) ?? { codice: buona.fornitore, nome, righe: 0, totale: 0 };
    gruppo.righe += 1;
    gruppo.totale += buona.quantita * buona.costo;
    per.set(buona.fornitore, gruppo);
  }

  return [...per.values()];
}

export type EsitoValidazione =
  | { ok: true; righe: RigaDaOrdinare[] }
  | { ok: false; errore: string };

/**
 * Cosa si manda al backend, oppure il primo motivo per cui non si può mandare.
 * Il dominio rifiuterebbe le stesse cose; dirle qui evita un giro di rete per scoprire
 * una cosa che si vede guardando la riga, e permette di nominare *quale* riga.
 */
export function preparaRighePerOrdine(
  righe: RigaDaPreparare[],
  decisioni: Record<string, Decisione>,
): EsitoValidazione {
  const daOrdinare: RigaDaOrdinare[] = [];

  for (const riga of righe) {
    if (!riga.ordinabile) continue;

    const decisione = decisioni[riga.rigaRichiestaId];
    if (!decisione || decisione.esclusa) continue;

    if (!decisione.fornitore) return { ok: false, errore: `«${riga.descrizione}»: manca il fornitore.` };

    const quantita = numeroDecimale(decisione.quantita);
    if (quantita === null)
      return { ok: false, errore: `«${riga.descrizione}»: manca la quantità.` };
    if (quantita <= 0)
      return { ok: false, errore: `«${riga.descrizione}»: la quantità deve essere maggiore di zero.` };

    const costo = numeroDecimale(decisione.costoNetto);
    if (costo === null)
      return { ok: false, errore: `«${riga.descrizione}»: manca il costo netto.` };
    if (costo < 0)
      return { ok: false, errore: `«${riga.descrizione}»: il costo netto non può essere negativo.` };

    daOrdinare.push({
      rigaRichiestaId: riga.rigaRichiestaId,
      codiceFornitoreMago: decisione.fornitore,
      quantita,
      costoNetto: costo,
      codiceArticoloFornitore: decisione.codiceArticoloFornitore,
    });
  }

  if (daOrdinare.length === 0)
    return { ok: false, errore: 'Nessuna riga da ordinare: le hai escluse tutte.' };

  return { ok: true, righe: daOrdinare };
}
